import {FC, ReactNode, useMemo, useState} from 'react'
import {GetContainer, renderToContainer} from '@/app/utils/render-to-container'
import {PropagationEvent, withStopPropagation} from '@/app/utils/with-stop-propagation'
import {mergeProps} from "@/app/utils/with-default-props"
import {ShouldRender} from "@/app/utils/should-render"
import {NativeProps, withNativeProps} from "@/app/utils/native-props"
import styles from './toast.module.css'

export type ToastProps = {
    afterClose?: () => void
    content?: ReactNode
    icon?: 'success' | 'loading' | 'fail' | ReactNode
    duration?: number
    position?: 'top' | 'center' | 'bottom'
    visible?: boolean
    getContainer?: GetContainer
    stopPropagation?: PropagationEvent[]
    destroyOnClose?: boolean
    forceRender?: boolean
} & NativeProps

const defaultProps = {
    getContainer: null,
    stopPropagation: ['click'],
    destroyOnClose: false,
    forceRender: false,
    visible: true,
}

export const InternalToast: FC<ToastProps> = p => {
    const props = mergeProps(defaultProps, p)
    const { content, icon, position } = props

    const iconElement = useMemo(() => {
        if (icon === null || icon === undefined) return null
        switch (icon) {
            case 'success':
                return <img src="/assets/images/icon_success.png" alt=""/>
            case 'loading':
                return <img src="/assets/images/icon_loading.png" alt=""/>
            case 'fail':
                return <img src="/assets/images/icon_fail.png" alt=""/>
            default:
                return icon
        }
    }, [icon])

    const top = useMemo(() => {
        switch (position) {
            case'top':
                return '20%'
            case 'bottom':
                return '80%'
            default:
                return '50%'
        }
    }, [position])

    const [active, setActive] = useState(props.visible)

    const node = withStopPropagation(
        props.stopPropagation,
        withNativeProps(
            props,
            <div className={styles.container} style={{top}}>
                <div className="toast-content">
                    {iconElement}
                    <div className="toast-text">{content}</div>
                </div>
            </div>
        )
    )

    return (
        <ShouldRender
            active={active}
            forceRender={props.forceRender}
            destroyOnClose={props.destroyOnClose}
        >
            {renderToContainer(props.getContainer, node)}
        </ShouldRender>
    )
}